import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import type { Solution } from '@app/src/context/AccessibleOrganizationsContext'
import { useAccessibleOrganizations } from '@app/src/context/AccessibleOrganizationsContext'
import type { AccountContextProps } from '@app/src/context/AccountContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import useLogin from '@app/src/hooks/login'
import { pathToRedirect } from '@app/src/pages/SolutionSelector/solutionSelectorUtils'
import { getSolutionTitle } from '@app/src/utils'
import { resolveCompanyLogoImageUrl } from '@app/src/utils/resolveCompanyLogoImageUrl'
import { Check, SyncAltOutlined, UnfoldMore } from '@mui/icons-material'
import {
  Badge,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import { buttonResetSx, navLabelSx } from './navItemStyles'
import { useVerticalMenu } from './VerticalMenuContext'

interface Props {
  account: AccountContextProps['account']
}

const NOTIFICATION_CHIP_SIZE = 24
const MAX_NOTIFICATION_COUNT = 99

const formatNotificationCount = (count: number): string =>
  count > MAX_NOTIFICATION_COUNT ? `${MAX_NOTIFICATION_COUNT}+` : String(count)

const NotificationDotBadge: React.FC<{ invisible: boolean; children: React.ReactNode }> = ({ invisible, children }) => (
  <Badge
    color="error"
    variant="dot"
    overlap="rectangular"
    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    sx={{ flexShrink: 0, '& .MuiBadge-badge': { backgroundColor: 'semantic.error' } }}
    invisible={invisible}
  >
    {children}
  </Badge>
)

const OrganizationAndSolutionMenu = ({ account }: Props): JSX.Element | null => {
  const { accessibleOrganizations } = useAccessibleOrganizations()
  const { formatMessage } = useIntl()
  const { scope } = useAuthentication()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [open, setOpen] = useState<boolean>(false)
  const history = useHistory()
  const login = useLogin()
  const { isExpanded, onPopoverOpen, onPopoverClose } = useVerticalMenu()

  useEffect(() => {
    if (!open) return
    onPopoverOpen()
    return onPopoverClose
  }, [open, onPopoverOpen, onPopoverClose])
  const { palette } = useTheme()

  const currentOrganizationSolutions = accessibleOrganizations.find(
    ao => ao?.organization?.id === account?.organization?.id,
  )
  const organization = currentOrganizationSolutions?.organization
  const organizationIdentifier = organization?.registrationNumber || organization?.vatNumber
  const organizationLogoUrl = organization ? resolveCompanyLogoImageUrl(organization) : undefined

  const hasUnseenNotificationsInOtherSolutions = Boolean(
    currentOrganizationSolutions?.solution?.some(
      s => s.solution.toLowerCase() !== scope.solution?.toLowerCase() && Boolean(s.unseenNotificationsCount),
    ),
  )

  const handleOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (): void => setOpen(false)

  const handleSolutionClick = async (solution: Solution): Promise<void> => {
    setOpen(false)

    if (solution?.solution?.toLowerCase() === scope.solution?.toLowerCase()) {
      solution && history.push(pathToRedirect(solution.solution))
      return
    }
    if (solution.role && solution.solution && account?.organization?.id) {
      await login(solution.role, solution.solution, account.organization.id)
      history.push(pathToRedirect(solution.solution))
      return
    }
  }

  const solutions = currentOrganizationSolutions?.solution ?? []
  const currentSolution = solutions.find(s => s.solution.toLowerCase() === scope?.solution?.toLowerCase())
  const otherSolutions = solutions
    .filter(s => s.solution.toLowerCase() !== scope?.solution?.toLowerCase())
    .sort((a, b) => a.solution.localeCompare(b.solution))
  const orderedSolutions = currentSolution ? [currentSolution, ...otherSolutions] : otherSolutions

  return (
    <>
      <Tooltip title={formatMessage({ id: 'organizationMenu.menuTooltip' })} placement="top" arrow>
        <Stack
          component="button"
          type="button"
          onClick={handleOpen}
          aria-haspopup="menu"
          aria-expanded={open}
          direction="row"
          alignItems="center"
          spacing={1}
          justifyContent="space-between"
          width="100%"
          p={1}
          sx={[
            buttonResetSx,
            {
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
              backgroundColor: open ? 'action.selected' : 'transparent',
              outline: open ? `1px solid ${palette.grey[300]}` : 'none',
              outlineOffset: open ? '-1px' : '0',
            },
          ]}
          borderRadius={2}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <NotificationDotBadge invisible={!hasUnseenNotificationsInOtherSolutions}>
              <Avatar
                variant="rounded"
                size={AvatarSize.Medium}
                src={organizationLogoUrl}
                sx={{ backgroundColor: palette.secondary.dark, color: palette.secondary.contrastText, flexShrink: 0 }}
              >
                {account?.organization?.name[0] ?? ''}
              </Avatar>
            </NotificationDotBadge>
            <Typography variant="subtitle1" color="text.primary" ml={1} noWrap sx={navLabelSx(isExpanded)}>
              {account?.organization?.name}
            </Typography>
          </Stack>

          <UnfoldMore color="action" sx={[{ fontSize: '20px', flexShrink: 0 }, navLabelSx(isExpanded)]} />
        </Stack>
      </Tooltip>

      <Menu
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
        sx={{ width: 280, '& .MuiPaper-root': { width: '100%', borderRadius: 2 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} px={2} py={1.5}>
          <Avatar
            variant="rounded"
            size={AvatarSize.Large}
            src={organizationLogoUrl}
            sx={{ backgroundColor: palette.secondary.dark, color: palette.secondary.contrastText, flexShrink: 0 }}
          >
            {account?.organization?.name[0] ?? ''}
          </Avatar>
          <Stack sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle1" color="text.primary" noWrap>
              {account?.organization?.name}
            </Typography>
            {organizationIdentifier && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {organizationIdentifier}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Typography variant="overline" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
          {formatMessage({ id: 'general.Solution' })}
        </Typography>

        {orderedSolutions.map(solution => {
          const isCurrentSolution = solution.solution.toLowerCase() === scope?.solution?.toLowerCase()
          return (
            <MenuItem key={solution.solution} onClick={() => handleSolutionClick(solution)}>
              <Avatar
                variant="circular"
                size={AvatarSize.Small}
                sx={{ backgroundColor: 'secondary.main', flexShrink: 0, mr: 1 }}
              >
                {getSolutionTitle(solution.solution).charAt(0) ?? ''}
              </Avatar>
              <ListItemText sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                  <Typography variant="subtitle2" color="text.secondary" noWrap>
                    {getSolutionTitle(solution.solution)}
                  </Typography>
                  {Boolean(solution.unseenNotificationsCount) && !isCurrentSolution && (
                    <Chip
                      sx={{
                        backgroundColor: 'semantic.error',
                        color: 'white',
                        borderRadius: `${NOTIFICATION_CHIP_SIZE / 2}px`,
                        flexShrink: 0,
                        minWidth: NOTIFICATION_CHIP_SIZE,
                        height: NOTIFICATION_CHIP_SIZE,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                      label={formatNotificationCount(solution.unseenNotificationsCount ?? 0)}
                      size="small"
                    />
                  )}
                </Stack>
              </ListItemText>
              {isCurrentSolution && (
                <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: '24px' } }}>
                  <Check fontSize="small" />
                </ListItemIcon>
              )}
            </MenuItem>
          )
        })}

        <Divider />

        <MenuItem
          onClick={handleClose}
          component={Link}
          to="/"
          sx={{ '& .MuiListItemIcon-root': { minWidth: '32px' } }}
        >
          <ListItemIcon>
            <SyncAltOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="subtitle2" color="text.secondary">
              {formatMessage({ id: 'accountMenu.solutionSelector' })}
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

export default OrganizationAndSolutionMenu
